import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabDocCommitEvent } from '@common/collab/events/collab-doc-commit.event';
import { CollabDocDeleteEvent } from '@common/collab/events/collab-doc-delete.event';
import { CollabDocMergeEvent } from '@common/collab/events/collab-doc-merge.event';
import { CollabDocRenameEvent } from '@common/collab/events/collab-doc-rename.event';
import { CollabDocRestoreEvent } from '@common/collab/events/collab-doc-restore.event';
import { CollabUserJSON } from '@common/collab/models/collab-user-json.model';
import { DocTimelineUpdate } from '@common/collab/types/doc-timeline-update.type';
import { FlareSchema } from '@common/flare/flare-schema';
import { CollabSessionOptions } from '@common/sockets/collab-session';
import { CollabCodeEditorComponent } from '@portal-core/code-editor/components/collab-code-editor/collab-code-editor.component';
import { FlareCodeChangesRecreatorTaskWorker } from '@portal-core/code-editor/util/flare-code-changes-recreator.task-worker';
import { FlareDocStepsRecreatorTaskWorker } from '@portal-core/code-editor/util/flare-doc-steps-recreator.task-worker';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { FlareTextEditorComponent } from '@portal-core/project-files/components/flare-text-editor/flare-text-editor.component';
import { FlareXmlEditorMode } from '@portal-core/project-files/types/flare-xml-editor-mode.type';

@Component({
  selector: 'mc-flare-collab-editor',
  templateUrl: './flare-collab-editor.component.html',
  styleUrls: ['./flare-collab-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlareCollabEditorComponent implements OnChanges {
  @Input() branchName: string;
  @Input() commitId: string;
  @Input() content?: string;
  @Input() fileId: number;
  @Input() filePath: string;
  @Input() fileType: CollabFileType;
  @Input() getTimelineDocVersion?: CollabSessionOptions['getTimelineDocVersion'];
  @Input() licenseUser: LicenseUser;
  @Input() mode?: FlareXmlEditorMode = 'text';
  @Input() projectId: number;
  @Input() projectLanguage: string;
  @Input() readonly?: boolean = false;
  @Input() showConditions?: boolean;
  @Input() trackedChanges?: boolean = false;
  @Input() timelineEnabled?: boolean = false;
  @Input() wrapLines?: boolean;

  @Output() docCommit: EventEmitter<CollabDocCommitEvent> = new EventEmitter<CollabDocCommitEvent>();
  @Output() docDelete: EventEmitter<CollabDocDeleteEvent> = new EventEmitter<CollabDocDeleteEvent>();
  @Output() docMerge: EventEmitter<CollabDocMergeEvent> = new EventEmitter<CollabDocMergeEvent>();
  @Output() docRename: EventEmitter<CollabDocRenameEvent> = new EventEmitter<CollabDocRenameEvent>();
  @Output() docRestore: EventEmitter<CollabDocRestoreEvent> = new EventEmitter<CollabDocRestoreEvent>();
  @Output() load: EventEmitter<void> = new EventEmitter<void>();
  @Output() navToFile: EventEmitter<string> = new EventEmitter<string>();
  @Output() timelineUpdate: EventEmitter<DocTimelineUpdate> = new EventEmitter<DocTimelineUpdate>();
  @Output() trackedChangesChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() unload: EventEmitter<void> = new EventEmitter<void>();
  @Output() usersUpdate: EventEmitter<CollabUserJSON[]> = new EventEmitter<CollabUserJSON[]>();
  @Output() wrapLinesChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() showConditionsChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild(FlareTextEditorComponent, { static: true }) flareTextEditor: FlareTextEditorComponent;
  @ViewChild(CollabCodeEditorComponent, { static: true }) collabCodeEditor: CollabCodeEditorComponent;

  /** Whether or not the code editor is loaded. Used for hiding the collab-doc-bar content until the data is ready. */
  codeEditorLoaded: boolean = false;
  /** The code changes recreator for syncing the collab session doc to the code editor. */
  flareCodeChangesRecreatorTaskWorker: FlareCodeChangesRecreatorTaskWorker = new FlareCodeChangesRecreatorTaskWorker();
  /** The doc steps recreator for syncing the code editor to the collab session. */
  flareDocStepsRecreatorTaskWorker: FlareDocStepsRecreatorTaskWorker = new FlareDocStepsRecreatorTaskWorker();
  /** The schema for the editors. */
  schema: FlareSchema = new FlareSchema();
  /** Whether or not the text editor is loaded. Used for hiding the collab-doc-bar content until the data is ready. */
  textEditorLoaded: boolean = false;

  /** The collab doc id in the collab system. */
  public get docId(): string {
    return this.flareTextEditor?.docId;
  }

  /** The doc version the doc is at. */
  public get docVersion(): number {
    return this.mode === 'text' ? this.flareTextEditor?.docVersion : this.collabCodeEditor?.docVersion;
  }

  ngOnChanges(changes: SimpleChanges) {
    // If the mode changed to the content tab then reflow the toolbar layout to account for viewport resizing while on the code tab
    if (changes.mode) {
      if (this.mode === 'text') {
        // Wait for the next tick to reflow the layout to ensure the text editor is showing
        setTimeout(() => {
          this.flareTextEditor.reflowToolbarLayout();
          this.flareTextEditor.reflowGutterLayout();
        });
      }
    }
  }

  onCodeEditorContentChanged() {
    // If the code editor content has changed then we know its been loaded. Really only the first change is needed to trigger the loaded state.
    this.codeEditorLoaded = true;
  }

  onCodeEditorUnloaded() {
    this.codeEditorLoaded = false;
  }

  onTextEditorLoaded() {
    this.textEditorLoaded = true;
    this.load.emit();
  }

  onTextEditorUnloaded() {
    this.textEditorLoaded = false;
    this.unload.emit()
  }

  onShowConditionsChanged(showConditions: boolean) {
    this.showConditions = showConditions;
    this.showConditionsChange.emit(showConditions);
  }

  onTrackedChangesChanged(trackedChanges: boolean) {
    this.trackedChanges = trackedChanges;
    this.trackedChangesChange.emit(trackedChanges);
  }

  onWrapLinesChanged(wrapLines: boolean) {
    this.wrapLines = wrapLines;
    this.wrapLinesChange.emit(wrapLines);
  }

  public reload() {
    this.flareTextEditor.reload();
    this.collabCodeEditor.reload();
  }
}
